import i18n from '@/i18n';
import {
  ADMIN_LIKE_ROLES,
  isAdminLike,
  isRestrictedOnly,
  ADMIN_ROLE,
  SUPERADMIN_ROLE,
  OPERATOR_ROLE,
} from '@/helpers/CONSTANTS';

/**
 * Lista "maestra" de items del sidebar. Refleja la estructura original
 * (antes del refactor) pero usa helpers de visibilidad reactivos
 * (`isAdminLike`, `isRestrictedOnly`) en lugar de comparaciones
 * hardcoded contra `ADMIN_ROLE`.
 *
 * Reglas de visibilidad (decision de producto - opcion A: todo para SUPERADMIN):
 *   - ADMIN_LIKE (SUPERADMIN, ADMIN, ADMIN_OPERATOR, PARTNER): ve todo.
 *   - RESTRICTED (OPERATOR): ve solo /operators y reportes restringidos.
 *   - Resto (CLIENT, CRUPIER): ve lo minimo (informe, ayuda, soporte).
 */
const buildMenu = (role) => {
  const admin = isAdminLike(role);
  const restricted = isRestrictedOnly(role);

  // Items administrativos: visibles para admin-like y, en algunos casos, restricted.
  const visibleAdminItem = () => ({ required: { beAdmin: false } });

  // Items exclusivos de roles restringidos (ej. /operators para OPERATOR).
  const visibleRestrictedOnlyItem = () => ({ required: { beAdmin: !admin } });
  const visibleRestrictedOrAdminItem = () => ({ required: { beAdmin: !(admin || restricted) } });

  // Items de pagos manuales: solo ADMIN y SUPERADMIN (ni OPERATOR, ni CLIENT,
  // ni ADMIN_OPERATOR/PARTNER).
  const isAdminOrSuperAdmin =
    role === ADMIN_ROLE || role === SUPERADMIN_ROLE;
  const visibleAdminOrSuperItem = () => ({
    required: { beAdmin: !isAdminOrSuperAdmin },
  });

  return [
    {
      id: 1,
      label: 'Menu',
      isTitle: true,
    },
    {
      id: 2,
      label: i18n.t('sidebar.home'),
      icon: 'uil-home-alt',
      link: '/',
    },

    {
      id: 4,
      label: i18n.t('sidebar.currency'),
      icon: 'bx bx-money',
      link: '/currencies',
      required: visibleAdminItem(),
    },
    {
      id: 5,
      label: i18n.t('sidebar.clients'),
      icon: 'mdi mdi-account-box-multiple',
      link: '/clients',
      required: visibleAdminItem(),
    },
    {
      id: 6,
      label: i18n.t('sidebar.operators'),
      icon: 'mdi mdi-desktop-classic',
      link: '/operators',
      required: visibleRestrictedOnlyItem(),
    },
    {
      id: 7,
      label: i18n.t('sidebar.players'),
      icon: 'mdi mdi-nintendo-game-boy',
      link: '/players',
      required: visibleAdminItem(),
    },
    {
      id: 88,
      label: i18n.t('sidebar.games.text'),
      icon: 'uil-game-structure',
      required: visibleAdminItem(),
      subItems: [
        {
          id: 881,
          label: i18n.t('sidebar.games.subItem.roulettes'),
          link: '/roulettes',
          parentId: 88,
          icon: 'uil-cube',
          required: visibleAdminItem(),
        },
        {
          id: 882,
          label: i18n.t('sidebar.games.subItem.tigerDragon'),
          link: '/tigerDragon',
          parentId: 88,
          icon: 'uil-cube',
          required: visibleAdminItem(),
        },
        {
          id: 883,
          label: i18n.t('sidebar.games.subItem.externalGames'),
          link: '/external-games',
          parentId: 88,
          icon: 'uil-cube',
          required: visibleAdminItem(),
        },
        {
          id: 884,
          label: 'Rueda de la suerte',
          link: '/sprint-wheels',
          parentId: 88,
          icon: 'uil-cube',
          required: visibleAdminItem(),
        },
        {
          id: 885,
          label: 'Black Jack',
          link: '/black-jack',
          parentId: 88,
          icon: 'uil-cube',
          required: visibleAdminItem(),
        },
      ],
    },
    {
      id: 8822,
      label: i18n.t('sidebar.tournaments'),
      icon: 'uil-trophy',
      link: '/tournaments',
      required: visibleAdminItem(),
    },
    {
      id: 9,
      label: i18n.t('sidebar.users'),
      icon: 'uil-users-alt',
      link: '/users',
      required: visibleAdminItem(),
    },
    {
      id: 999,
      label: i18n.t('sidebar.manualPayment.text'),
      icon: 'uil-money-withdrawal',
      required: visibleAdminOrSuperItem(),
      subItems: [
        {
          id: 9991,
          label: i18n.t('sidebar.manualPayment.subItem.individual'),
          link: '/manual-payment/individual',
          parentId: 999,
          icon: 'uil-user',
          required: visibleAdminOrSuperItem(),
        },
        {
          id: 9992,
          label: i18n.t('sidebar.manualPayment.subItem.general'),
          link: '/manual-payment/general',
          icon: 'uil-user-plus',
          required: visibleAdminOrSuperItem(),
        },
      ],
    },
    {
      id: 10,
      label: i18n.t('sidebar.reports.text'),
      icon: 'dripicons-export',
      required: visibleRestrictedOrAdminItem(),
      subItems: [
        {
          id: 9,
          label: i18n.t('sidebar.reports.subItems.transactions'),
          link: '/reports/transations',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-transaction',
        },
        {
          id: 999,
          label: 'Historial crupier',
          link: '/reports/crupier-history',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-transaction',
        },
        {
          id: 992,
          label: 'Tickets',
          link: '/reports/tickets',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-ticket',
        },
        {
          id: 91,
          label: 'Jackpots',
          link: '/reports/jackpots',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-star',
        },
        {
          id: 10,
          label: i18n.t('sidebar.reports.subItems.historial'),
          link: '/reports/histories',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-history',
        },
        {
          id: 11,
          label: i18n.t('sidebar.reports.subItems.players'),
          link: '/reports/players',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-users-alt',
        },
        {
          id: 13,
          label: i18n.t('sidebar.reports.subItems.ggr'),
          link: '/reports/ggr',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-graph-bar',
        },
        {
          id: 132,
          label: i18n.t('sidebar.reports.subItems.ggrByCasino'),
          link: '/reports/ggr-by-casino',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-comparison',
        },
        {
          id: 1322,
          label: 'GGR BY WL',
          link: '/reports/ggr-by-wl',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-comparison',
        },
        {
          id: 14,
          label: i18n.t('sidebar.reports.subItems.rounds'),
          link: '/reports/rounds',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-adjust-half',
        },
        {
          id: 15,
          label: i18n.t('sidebar.reports.subItems.providerResults'),
          link: '/reports/provider-logs',
          parentId: 10,
          required: visibleAdminItem(),
          icon: 'uil-newspaper',
        },
        {
          id: 16,
          label: i18n.t('sidebar.reports.subItems.currencyFluctuation'),
          link: '/reports/currencies',
          parentId: 10,
          required: visibleAdminItem(),
          icon: 'uil-file-graph',
        },
        {
          id: 17,
          label: i18n.t('sidebar.reports.subItems.stockAudit'),
          link: '/reports/auditory',
          parentId: 10,
          required: visibleAdminItem(),
          icon: 'uil-comment-alt-chart-lines',
        },
        {
          id: 188,
          label: i18n.t('betLogs.title'),
          link: '/reports/bet-logs',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-comment-alt-chart-lines',
        },
        {
          id: 189,
          label: 'Visitas / Apuestas',
          link: '/reports/views-bets-by-round',
          parentId: 10,
          required: visibleRestrictedOrAdminItem(),
          icon: 'uil-calendar-alt',
        },
      ],
    },
    {
      id: 15,
      label: i18n.t('sidebar.technicalSupport'),
      icon: 'uil-comment-alt-info',
      link: '/technical-support',
    },
    {
      id: 16,
      label: i18n.t('sidebar.exchangeAPIs'),
      icon: 'uil-cloud-computing',
      link: '/exchange-apis',
      required: visibleAdminItem(),
    },
    {
      id: 17,
      label: i18n.t('sidebar.crupiers'),
      icon: 'uil-users-alt',
      link: '/crupiers',
      required: visibleAdminItem(),
    },
    {
      id: 17,
      label: i18n.t('sidebar.incidents'),
      icon: 'uil-comment-info-alt',
      link: '/incidents',
      required: visibleAdminItem(),
    },
    {
      id: 17,
      label: i18n.t('sidebar.help'),
      icon: 'uil-question-circle',
      link: '/help',
    },
    {
      id: 177,
      label: i18n.t('sidebar.roles'),
      icon: 'uil-user',
      link: '/roles',
    },
    {
      id: 198,
      label: 'QUEUES',
      icon: 'uil-graph-bar',
      link: '/queues',
      required: visibleAdminItem(),
    },
  ];
};

/**
 * Lista estatica del menu (sin filtrar por rol).
 * Mantenida por compatibilidad con consumers externos
 * (ej. helpers/filterMenu.js). El sidebar real consume el getter
 * `getMenu` que aplica la visibilidad segun el rol activo.
 */
export const menuItems = buildMenu(ADMIN_ROLE);

export const state = {
  menu: [],
  roles: [...ADMIN_LIKE_ROLES, OPERATOR_ROLE],
  roleViewAll: [...ADMIN_LIKE_ROLES],
  sideBarReduce: true,
  hoverExpand: true,
  openSideBar: true,
};

export const getters = {
  /**
   * Getter principal del sidebar. Construye el menu aplicando la visibilidad
   * segun el rol actual del store (reactivo).
   */
  getMenu: () => (role) => buildMenu(role),
  isSideBarReduce() {
    return state.sideBarReduce;
  },
  isHoverExpand() {
    return state.hoverExpand;
  },
  isOpenSideBar() {
    return state.openSideBar;
  },
};

export const mutations = {
  setMenu(state, payload) {
    state.menu = payload;
  },
  toggleSideBar(state) {
    state.openSideBar = !state.openSideBar;
    console.log(state.openSideBar);
  },
};

export const actions = {};
